import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

/**
 * Per-day API call counters with a hard ceiling. Belt-and-suspenders defense
 * against a runaway loop draining the user's Google quota. Stored on-device
 * (SecureStore on native, localStorage on web). Reset automatically at the
 * start of each UTC day so the ceilings are predictable.
 *
 * Counters are intentionally simple — we'd rather over-block than miss a
 * leak; calling sites can override the default 100/day per API.
 */

export type QuotaKey = 'vision' | 'places' | 'static_maps';

const KEY_PREFIX = 'homestead.quota.';
const DEFAULT_CEILING: Record<QuotaKey, number> = {
  vision: 100,
  places: 200,
  static_maps: 200,
};

interface DailyCounter {
  date: string; // YYYY-MM-DD UTC
  count: number;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

async function readRaw(key: string): Promise<string | null> {
  if (Platform.OS === 'web') {
    try {
      // eslint-disable-next-line no-undef
      return window.localStorage.getItem(key);
    } catch { return null; }
  }
  try { return await SecureStore.getItemAsync(key); } catch { return null; }
}

async function writeRaw(key: string, value: string): Promise<void> {
  if (Platform.OS === 'web') {
    try {
      // eslint-disable-next-line no-undef
      window.localStorage.setItem(key, value);
    } catch { /* ignore */ }
    return;
  }
  try { await SecureStore.setItemAsync(key, value); } catch { /* ignore */ }
}

async function readCounter(key: QuotaKey): Promise<DailyCounter> {
  const raw = await readRaw(KEY_PREFIX + key);
  const today = todayKey();
  if (!raw) return { date: today, count: 0 };
  try {
    const parsed = JSON.parse(raw) as DailyCounter;
    if (parsed.date !== today) return { date: today, count: 0 };
    return parsed;
  } catch {
    return { date: today, count: 0 };
  }
}

/**
 * Try to consume one call from the daily budget. Returns false if the
 * caller has already hit today's ceiling — they should surface a friendly
 * "daily limit reached" message rather than making the network call.
 */
export async function tryConsume(
  key: QuotaKey,
  ceiling: number = DEFAULT_CEILING[key]
): Promise<{ ok: boolean; used: number; ceiling: number }> {
  const counter = await readCounter(key);
  if (counter.count >= ceiling) {
    return { ok: false, used: counter.count, ceiling };
  }
  const next: DailyCounter = { date: counter.date, count: counter.count + 1 };
  await writeRaw(KEY_PREFIX + key, JSON.stringify(next));
  return { ok: true, used: next.count, ceiling };
}

export async function getUsage(key: QuotaKey): Promise<{ used: number; ceiling: number }> {
  const counter = await readCounter(key);
  return { used: counter.count, ceiling: DEFAULT_CEILING[key] };
}
